import { create } from 'zustand';
import { api, fromUnix, toQueryString } from '@/lib/api';
import type { ApiResponse, VoiceCall } from '@nexus/shared/types';
import { toast } from './toastStore';

interface VoiceState {
  calls: VoiceCall[];
  total: number;
  isLoading: boolean;
  error: string | null;
  fetch: (params?: Record<string, string | number | undefined>) => Promise<void>;
  create: (data: Record<string, unknown>) => Promise<VoiceCall>;
  update: (id: string, data: Record<string, unknown>) => Promise<void>;
  clearError: () => void;
}

function convert(c: Record<string, unknown>): VoiceCall {
  return { ...c, createdAt: fromUnix(c.createdAt as number) } as VoiceCall;
}

export const useVoiceStore = create<VoiceState>((set) => ({
  calls: [],
  total: 0,
  isLoading: false,
  error: null,

  fetch: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get<ApiResponse<VoiceCall[]>>(`/api/voice/calls${toQueryString(params)}`);
      set({
        calls: (res.data ?? []).map(c => convert(c as unknown as Record<string, unknown>)),
        total: res.meta?.total ?? 0,
        isLoading: false,
      });
    } catch (err: unknown) {
      set({ error: (err as Error).message, isLoading: false });
    }
  },

  create: async (data) => {
    try {
      const res = await api.post<ApiResponse<VoiceCall>>('/api/voice/calls', data);
      const call = convert(res.data as unknown as Record<string, unknown>);
      set((s) => ({ calls: [call, ...s.calls], total: s.total + 1 }));
      toast.success('Call initiated');
      return call;
    } catch (err) {
      toast.error('Failed to initiate call', (err as Error).message);
      throw err;
    }
  },

  update: async (id, data) => {
    try {
      const res = await api.patch<ApiResponse<VoiceCall>>(`/api/voice/calls/${id}`, data);
      const call = convert(res.data as unknown as Record<string, unknown>);
      set((s) => ({ calls: s.calls.map((c) => (c.id === id ? call : c)) }));
      toast.success('Call updated');
    } catch (err) {
      toast.error('Failed to update call', (err as Error).message);
      throw err;
    }
  },

  clearError: () => set({ error: null }),
}));
